import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = {
		hasError: false,
		error: null,
		errorInfo: null,
	};

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		this.setState({ error, errorInfo });
		if (this.props.onError) {
			try {
				this.props.onError(error, errorInfo);
			} catch {
				// swallow errors from onError handler
			}
		} else {
			// provide a sensible default logging

			console.error(error, errorInfo);
		}
	}

	reset = () => {
		this.setState({ hasError: false, error: null, errorInfo: null });
	};

	private renderDefaultFallback() {
		const { error, errorInfo } = this.state;
		const isDev = process.env.NODE_ENV === 'development';
		return (
			<Box
				role="alert"
				display="flex"
				alignItems="center"
				justifyContent="center"
				bgcolor="#fff5f5"
				fontFamily="sans-serif"
				sx={{ maxHeight: 'calc(100vh - 150px)' }}
			>
				<Card
					sx={{
						minWidth: 350,
						maxWidth: 500,
						mx: 'auto',
						boxShadow: 3,
						overflow: 'auto',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
					}}
				>
					<CardContent
						sx={{
							p: 3,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
						}}
					>
						<Typography variant="h1" color="error" gutterBottom>
							Error
						</Typography>
						<Typography variant="body1" gutterBottom>
							Oops — something went wrong. We&apos;re sorry for the inconvenience.
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Try reloading the page. If the problem continues, please contact support
							with any steps to reproduce and we&apos;ll look into it.
						</Typography>
						<Button
							onClick={this.reset}
							variant="contained"
							color="primary"
							sx={{ mb: 2 }}
						>
							Reload
						</Button>
						{isDev && (
							<Box
								component="pre"
								sx={{
									whiteSpace: 'pre-wrap',
									margin: 0,
									padding: 2,
									bgcolor: '#fff',
									border: '1px solid #eee',
									borderRadius: 1,
									fontSize: 12,
									maxWidth: 400,
									color: '#2d2d2d',
									overflow: 'auto',
									maxHeight: 150,
								}}
							>
								{error?.toString()}
								{errorInfo?.componentStack
									? `\n\n${errorInfo.componentStack}`
									: null}
							</Box>
						)}
					</CardContent>
				</Card>
			</Box>
		);
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback ?? this.renderDefaultFallback();
		}
		return this.props.children as ReactNode;
	}
}
